import { getApiUrl } from '@/constants';
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { ResetIndexedDb } from '../../utils/ResetIndexedDb';
import { loginSuccess, setIsUpdating } from '../reducers/authReducer';

// Custom base query that replicates axios interceptor logic with fetch
const baseQueryWithAuth = fetchBaseQuery({
  baseUrl: getApiUrl(),
  timeout: 200000,
  prepareHeaders: (headers, { getState }) => {
    // Get token from auth state (same as interceptors)
    const token = getState()?.auth?.currentUser?.token?.token;
    if (token) {
      headers.set('token', token);
    }

    // Get customer ID and site ID from Redux state (same as interceptors)
    const customerID = getState()?.customer?.currentCustomer?.customerID;
    const siteID = getState()?.site?.currentSite?.key;

    if (customerID) {
      headers.set('x-customer-id', customerID);
    }
    if (siteID) {
      headers.set('x-site-id', siteID);
    }

    return headers;
  },
});

// Enhanced base query that handles token refresh and auth errors
const enhancedBaseQuery = async (args, api, extraOptions) => {
  let result = await baseQueryWithAuth(args, api, extraOptions);

  // Handle authentication errors (similar to your axios interceptors)
  if (result.error && result.error.status === 412) {
    const errorDetail = result.error.data?.detail;
    if (errorDetail === 'Token Expired' || errorDetail === 'Not authenticated') {
      // Check if we're already updating credentials
      const isUpdating = api.getState()?.auth?.isUpdating;

      if (!isUpdating) {
        // Trigger token refresh
        try {
          const refreshResult = await handleTokenRefresh(api);
          if (refreshResult.success) {
            // Retry the original request with new token
            result = await baseQueryWithAuth(args, api, extraOptions);
          } else {
            // Handle logout
            handleLogout();
          }
        } catch (error) {
          // Handle logout on refresh failure
          handleLogout();
        }
      } else {
        // Wait for token refresh to complete
        result = await waitForTokenRefresh(args, api, extraOptions);
      }
    }
  }

  return result;
};

// Token refresh function (replicates your axios interceptor logic)
const handleTokenRefresh = async (api) => {
  const state = api.getState();
  const { token, refresh } = state.auth.currentUser;

  // Set updating flag
  api.dispatch(setIsUpdating(true));

  try {
    const response = await fetch(`${getApiUrl()}/auth/creds`, {
      method: 'GET',
      headers: {
        token: token?.token,
        refresh: refresh,
      },
    });

    if (response.ok) {
      const data = await response.json();

      // Update auth state using Redux action
      api.dispatch(loginSuccess(data));

      // Update localStorage
      localStorage.setItem('token', data);

      return { success: true, data };
    } else {
      return { success: false };
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
    return { success: false };
  } finally {
    // Clear updating flag
    api.dispatch(setIsUpdating(false));
  }
};

// Wait for token refresh to complete
const waitForTokenRefresh = async (args, api, extraOptions) => {
  return new Promise((resolve) => {
    const intervalId = setInterval(() => {
      const state = api.getState();
      if (!state.auth.isUpdating && state.auth.currentUser.token) {
        clearInterval(intervalId);
        resolve(baseQueryWithAuth(args, api, extraOptions));
      }
    }, 1000);
  });
};

// Logout function (replicates your axios interceptor logic)
const handleLogout = () => {
  console.log('Handling logout...');

  // Use your existing ResetIndexedDb function
  ResetIndexedDb('handleLogout');

  // Clear cache
  clearCacheData();

  // Clear IndexedDB
  window.indexedDB.deleteDatabase('STN');

  // Clear storage
  localStorage.clear();
  sessionStorage.clear();

  // Redirect to login
  window.history.pushState({}, '', '/login');
  window.location.reload();
};

// Cache clearing function (replicates your axios interceptor logic)
const clearCacheData = async () => {
  try {
    const cacheNames = await window.caches.keys();
    await Promise.all(cacheNames.map((cacheName) => window.caches.delete(cacheName)));
    clearAllCookies();
    sessionStorage.clear();
    localStorage.clear();
    console.log('Complete Cache Cleared');
  } catch (error) {
    console.error('Failed to clear cache:', error);
  }
};

// Cookie clearing function (replicates your axios interceptor logic)
function clearAllCookies() {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;`;
  }
}

export default enhancedBaseQuery;
